import { makeProcessOriginExporter } from './processOriginExporter';
import {
  List,
  Create,
  Edit,
  SimpleForm,
  TextInput,
  TextField,
  EditButton,
  DateField,
  Resource,
  Show,
  ReferenceInput,
  ReferenceField,
  AutocompleteInput,
  Datagrid,
  SimpleShowLayout,
  SelectInput,
  FunctionField,
  FormDataConsumer
} from "react-admin";
import EllipsisTextField from "../components/EllipsisTextField";
import VersionedList, { DbVersionFilterInput, DbVersionField, DbVersionInput } from "../components/DbVersion";
import { ENERGY_TYPE_OPTIONS } from "../consts";

// Componentes generales para Process Origin
const ProcessOriginList = (props) => (
  <List {...props} resource="processOrigin">
    <div style={{ marginTop: '15px' }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <ReferenceField label="Name" source="processId" reference="process">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Origin" source="originId" reference="origins">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Database Context" source="databaseContextId" reference="database-context" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="description" />
        <TextField source="primarySource" label="Primary source" />
        <TextField source="dataGenerator" label="Data generator" />
        <TextField source="water" />
        <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
        <TextField source="eutrophication" />
        <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
        <EditButton />
      </Datagrid>
    </div>
  </List>
);

const ShowProcessOrigin = (props) => (
  <Show {...props} resource="processOrigin">
    <SimpleShowLayout>
      <TextField source="id" />
      <ReferenceField label="Process" source="processId" reference="process">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Origin" source="originId" reference="origins">
        <TextField source="name" />
      </ReferenceField>
      <TextField source="description" />
      <TextField source="primarySource" label="Primary source" />
      <TextField source="dataGenerator" label="Data generator" />
      <TextField source="impactGenerator" label="Impact generator" />
      <TextField source="originalDataset" label="Original dataset" />
      <TextField source="systemBoundaries" label="System boundaries" />
      <TextField source="allocation" />
      <TextField source="mainAssumptions" label="Main assumptions" />
      <TextField source="water" />
      <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
      <TextField source="eutrophication" />
      <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
      <DateField source="createdAt" label="Creation date" />
      <DateField source="updatedAt" label="Update date" />
    </SimpleShowLayout>
  </Show>
);

const CreateProcessOrigin = (props) => (
  <Create {...props} resource="processOrigin">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Process" source="processId" reference="process" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <TextInput source="description" />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" />
      <TextInput source="systemBoundaries" label="System boundaries" />
      <TextInput source="allocation" />
      <TextInput source="mainAssumptions" label="Main assumptions" />
      <TextInput source="water" required={true} />
      <TextInput source="climateChange" required={true} />
      <TextInput source="eutrophication" required={true} />
      <TextInput source="fossilFuels" required={true} />
    </SimpleForm>
  </Create>
);

const EditProcessOrigin = (props) => (
  <Edit {...props} resource="processOrigin">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Process" source="processId" reference="process" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <TextInput source="description" />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" />
      <TextInput source="systemBoundaries" label="System boundaries" />
      <TextInput source="allocation" />
      <TextInput source="mainAssumptions" label="Main assumptions" />
      <TextInput source="water" required={true} />
      <TextInput source="climateChange" required={true} />
      <TextInput source="eutrophication" required={true} />
      <TextInput source="fossilFuels" required={true} />
    </SimpleForm>
  </Edit>
);

// Componentes para cada tipo de proceso origen
// Extraction Process Origin
const ExtractionFilters = [
  <ReferenceInput
    key="extractionNameSearch"
    label="Search by name"
    source="extractionId"
    reference="process/extraction"
    alwaysOn
    filterToQuery={searchText => ({ name: searchText })}
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
  <ReferenceInput
    key="extractionAccountSearch"
    label="Search by account"
    source="accountId"
    reference="accounts"
    alwaysOn
    filterToQuery={searchText => ({ name: searchText })}
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
  <DbVersionFilterInput key="extractionOriginDbVersion" alwaysOn />
];

const ExtractionOriginList = (props) => (
  <VersionedList {...props} resource="processOrigin/extraction" filters={ExtractionFilters} exporter={makeProcessOriginExporter('extraction')}>
    <div style={{ marginTop: '15px' }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <ReferenceField label="Name" source="extractionId" reference="process/extraction">
          <TextField source="name" />
        </ReferenceField>
        <DbVersionField />
        <ReferenceField label="Origin" source="originId" reference="origins">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Database Context" source="databaseContextId" reference="database-context" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="energyType" label="Energy Type" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="water" label="Water scarcity (m3 water eq)" />
        <TextField source="waterConsumption" label="Water consumption (liters water)" />
        <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
        <TextField source="eutrophication" label="Eutrophication (g P eq)" />
        <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
        <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
        <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
        <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
        <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
        <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
        <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
        <TextField source="acidification" label="Acidification (mol H+ eq)" />
        <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
        <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
        <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
        <TextField source="landUse" label="Land Use (pt)" />
        <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
        <EllipsisTextField source="description" label="Description" />
        <EllipsisTextField source="primarySource" label="Primary source" />
        <EllipsisTextField source="dataGenerator" label="Data generator" />
        <EllipsisTextField source="impactGenerator" label="Impact generator" />
        <EllipsisTextField source="originalDataset" label="Original dataset" />
        <EllipsisTextField source="systemBoundaries" label="System boundaries" />
        <EllipsisTextField source="allocation" label="Allocation" />
        <EllipsisTextField source="mainAssumptions" label="Main assumptions" />
        <TextField source="dataValidity" label="Data validity" />
        <TextField source="dataQuality" label="Data quality" />
        <TextField source="referenceYear" label="Reference year" />
        <TextField source="teR" label="teR" />
        <TextField source="geR" label="geR" />
        <TextField source="tiR" label="tiR" />
        <TextField source="precision" label="Precision" />
        <TextField source="type" label="Type" />
        <ReferenceField label="Account" source="accountId" reference="accounts" emptyText="" link={false}>
          <FunctionField render={record => record ? `${record.name} (${record.id})` : ''} />
        </ReferenceField>
        <EditButton />
      </Datagrid>
    </div>
  </VersionedList>
);

const ShowExtractionOrigin = (props) => (
  <Show {...props} resource="processOrigin/extraction">
    <SimpleShowLayout>
      <TextField source="id" />
      <ReferenceField label="Name" source="extractionId" reference="process/extraction">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Origin" source="originId" reference="origins">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Database Context" source="databaseContextId" reference="database-context">
        <TextField source="name" />
      </ReferenceField>
      <TextField source="energyType" label="Energy Type" />
      <DateField source="createdAt" label="Creation date" />
      <DateField source="updatedAt" label="Update date" />
      <TextField source="water" label="Water scarcity (m3 water eq)" />
      <TextField source="waterConsumption" label="Water consumption (liters water)" />
      <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
      <TextField source="eutrophication" label="Eutrophication (g P eq)" />
      <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
      <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextField source="acidification" label="Acidification (mol H+ eq)" />
      <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextField source="landUse" label="Land Use (pt)" />
      <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
      <TextField source="description" label="Description" />
      <TextField source="primarySource" label="Primary source" />
      <TextField source="dataGenerator" label="Data generator" />
      <TextField source="impactGenerator" label="Impact generator" />
      <TextField source="originalDataset" label="Original dataset" />
      <TextField source="systemBoundaries" label="System boundaries" />
      <TextField source="allocation" label="Allocation" />
      <TextField source="mainAssumptions" label="Main assumptions" />
      <TextField source="dataValidity" label="Data validity" />
      <TextField source="dataQuality" label="Data quality" />
      <TextField source="referenceYear" label="Reference year" />
      <TextField source="teR" label="teR" />
      <TextField source="geR" label="geR" />
      <TextField source="tiR" label="tiR" />
      <TextField source="precision" label="Precision" />
      <TextField source="type" label="Type" />
      <ReferenceField label="Account" source="accountId" reference="accounts" emptyText="" link={false}>
        <FunctionField render={record => record ? `${record.name} (${record.id})` : ''} />
      </ReferenceField>
    </SimpleShowLayout>
  </Show>
);

const CreateExtractionOrigin = (props) => (
  <Create {...props} resource="processOrigin/extraction">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Name" source="extractionId" reference="process/extraction" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Database Context" source="databaseContextId" reference="database-context" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <SelectInput source="energyType" label="Energy Type" defaultValue="Conventional" choices={ENERGY_TYPE_OPTIONS} />
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={[
        { id: 'Generic', name: 'Generic' },
        { id: 'Custom', name: 'Custom' },
        { id: 'Specific', name: 'Specific' },
        { id: 'Multi-process', name: 'Multi-process' }
      ]} />
      <FormDataConsumer>
        {({ formData }) => formData.type === 'Custom' && (
          <ReferenceInput label="Account" source="accountId" reference="accounts" filterToQuery={searchText => ({ name: searchText })}>
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
        )}
      </FormDataConsumer>
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Create>
);

const EditExtractionOrigin = (props) => (
  <Edit {...props} resource="processOrigin/extraction">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Name" source="extractionId" reference="process/extraction" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Database Context" source="databaseContextId" reference="database-context" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <SelectInput source="energyType" label="Energy Type" defaultValue="Conventional" choices={ENERGY_TYPE_OPTIONS} />
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={[
        { id: 'Generic', name: 'Generic' },
        { id: 'Custom', name: 'Custom' },
        { id: 'Specific', name: 'Specific' },
        { id: 'Multi-process', name: 'Multi-process' }
      ]} />
      <FormDataConsumer>
        {({ formData }) => formData.type === 'Custom' && (
          <ReferenceInput label="Account" source="accountId" reference="accounts" filterToQuery={searchText => ({ name: searchText })}>
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
        )}
      </FormDataConsumer>
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Edit>
);

// Processing Process Origin
const ProcessingFilters = [
  <ReferenceInput
    key="processingNameSearch"
    label="Search by name"
    source="processingId"
    reference="process/processing"
    alwaysOn
    filterToQuery={searchText => ({ name: searchText })}
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
  <ReferenceInput
    key="processingAccountSearch"
    label="Search by account"
    source="accountId"
    reference="accounts"
    alwaysOn
    filterToQuery={searchText => ({ name: searchText })}
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
  <DbVersionFilterInput key="processingOriginDbVersion" alwaysOn />
];

const ProcessingOriginList = (props) => (
  <VersionedList {...props} resource="processOrigin/processing" filters={ProcessingFilters} exporter={makeProcessOriginExporter('processing')}>
    <div style={{ marginTop: '15px' }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <ReferenceField label="Name" source="processingId" reference="process/processing">
          <TextField source="name" />
        </ReferenceField>
        <DbVersionField />
        <ReferenceField label="Origin" source="originId" reference="origins">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Database Context" source="databaseContextId" reference="database-context" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="energyType" label="Energy Type" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="water" label="Water scarcity (m3 water eq)" />
        <TextField source="waterConsumption" label="Water consumption (liters water)" />
        <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
        <TextField source="eutrophication" label="Eutrophication (g P eq)" />
        <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
        <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
        <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
        <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
        <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
        <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
        <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
        <TextField source="acidification" label="Acidification (mol H+ eq)" />
        <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
        <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
        <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
        <TextField source="landUse" label="Land Use (pt)" />
        <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
        <EllipsisTextField source="description" label="Description" />
        <EllipsisTextField source="primarySource" label="Primary source" />
        <EllipsisTextField source="dataGenerator" label="Data generator" />
        <EllipsisTextField source="impactGenerator" label="Impact generator" />
        <EllipsisTextField source="originalDataset" label="Original dataset" />
        <EllipsisTextField source="systemBoundaries" label="System boundaries" />
        <EllipsisTextField source="allocation" label="Allocation" />
        <EllipsisTextField source="mainAssumptions" label="Main assumptions" />
        <TextField source="dataValidity" label="Data validity" />
        <TextField source="dataQuality" label="Data quality" />
        <TextField source="referenceYear" label="Reference year" />
        <TextField source="teR" label="teR" />
        <TextField source="geR" label="geR" />
        <TextField source="tiR" label="tiR" />
        <TextField source="precision" label="Precision" />
        <TextField source="type" label="Type" />
        <ReferenceField label="Account" source="accountId" reference="accounts" emptyText="" link={false}>
          <FunctionField render={record => record ? `${record.name} (${record.id})` : ''} />
        </ReferenceField>
        <EditButton />
      </Datagrid>
    </div>
  </VersionedList>
);

const ShowProcessingOrigin = (props) => (
  <Show {...props} resource="processOrigin/processing">
    <SimpleShowLayout>
      <TextField source="id" />
      <ReferenceField label="Name" source="processingId" reference="process/processing">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Origin" source="originId" reference="origins">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Database Context" source="databaseContextId" reference="database-context">
        <TextField source="name" />
      </ReferenceField>
      <TextField source="energyType" label="Energy Type" />
      <DateField source="createdAt" label="Creation date" />
      <DateField source="updatedAt" label="Update date" />
      <TextField source="water" label="Water scarcity (m3 water eq)" />
      <TextField source="waterConsumption" label="Water consumption (liters water)" />
      <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
      <TextField source="eutrophication" label="Eutrophication (g P eq)" />
      <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
      <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextField source="acidification" label="Acidification (mol H+ eq)" />
      <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextField source="landUse" label="Land Use (pt)" />
      <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
      <TextField source="description" label="Description" />
      <TextField source="primarySource" label="Primary source" />
      <TextField source="dataGenerator" label="Data generator" />
      <TextField source="impactGenerator" label="Impact generator" />
      <TextField source="originalDataset" label="Original dataset" />
      <TextField source="systemBoundaries" label="System boundaries" />
      <TextField source="allocation" label="Allocation" />
      <TextField source="mainAssumptions" label="Main assumptions" />
      <TextField source="dataValidity" label="Data validity" />
      <TextField source="dataQuality" label="Data quality" />
      <TextField source="referenceYear" label="Reference year" />
      <TextField source="teR" label="teR" />
      <TextField source="geR" label="geR" />
      <TextField source="tiR" label="tiR" />
      <TextField source="precision" label="Precision" />
      <TextField source="type" label="Type" />
      <ReferenceField label="Account" source="accountId" reference="accounts" emptyText="" link={false}>
        <FunctionField render={record => record ? `${record.name} (${record.id})` : ''} />
      </ReferenceField>
    </SimpleShowLayout>
  </Show>
);

const CreateProcessingOrigin = (props) => (
  <Create {...props} resource="processOrigin/processing">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Name" source="processingId" reference="process/processing" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Database Context" source="databaseContextId" reference="database-context" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <SelectInput source="energyType" label="Energy Type" defaultValue="Conventional" choices={ENERGY_TYPE_OPTIONS} />
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={[
        { id: 'Generic', name: 'Generic' },
        { id: 'Custom', name: 'Custom' },
        { id: 'Specific', name: 'Specific' },
        { id: 'Multi-process', name: 'Multi-process' }
      ]} />
      <FormDataConsumer>
        {({ formData }) => formData.type === 'Custom' && (
          <ReferenceInput label="Account" source="accountId" reference="accounts" filterToQuery={searchText => ({ name: searchText })}>
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
        )}
      </FormDataConsumer>
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Create>
);

const EditProcessingOrigin = (props) => (
  <Edit {...props} resource="processOrigin/processing">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Name" source="processingId" reference="process/processing" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Database Context" source="databaseContextId" reference="database-context" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <SelectInput source="energyType" label="Energy Type" defaultValue="Conventional" choices={ENERGY_TYPE_OPTIONS} />
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={[
        { id: 'Generic', name: 'Generic' },
        { id: 'Custom', name: 'Custom' },
        { id: 'Specific', name: 'Specific' },
        { id: 'Multi-process', name: 'Multi-process' }
      ]} />
      <FormDataConsumer>
        {({ formData }) => formData.type === 'Custom' && (
          <ReferenceInput label="Account" source="accountId" reference="accounts" filterToQuery={searchText => ({ name: searchText })}>
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
        )}
      </FormDataConsumer>
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Edit>
);

// Finishing Process Origin
const FinishingFilters = [
  <ReferenceInput
    key="finishingNameSearch"
    label="Search by name"
    source="finishingId"
    reference="process/finishing"
    alwaysOn
    filterToQuery={searchText => ({ name: searchText })}
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
  <ReferenceInput
    key="finishingAccountSearch"
    label="Search by account"
    source="accountId"
    reference="accounts"
    alwaysOn
    filterToQuery={searchText => ({ name: searchText })}
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
  <DbVersionFilterInput key="finishingOriginDbVersion" alwaysOn />
];

const FinishingOriginList = (props) => (
  <VersionedList {...props} resource="processOrigin/finishing" filters={FinishingFilters} exporter={makeProcessOriginExporter('finishing')}>
    <div style={{ marginTop: '15px' }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <ReferenceField label="Name" source="finishingId" reference="process/finishing">
          <TextField source="name" />
        </ReferenceField>
        <DbVersionField />
        <ReferenceField label="Origin" source="originId" reference="origins">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Database Context" source="databaseContextId" reference="database-context" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="energyType" label="Energy Type" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="water" label="Water scarcity (m3 water eq)" />
        <TextField source="waterConsumption" label="Water consumption (liters water)" />
        <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
        <TextField source="eutrophication" label="Eutrophication (g P eq)" />
        <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
        <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
        <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
        <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
        <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
        <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
        <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
        <TextField source="acidification" label="Acidification (mol H+ eq)" />
        <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
        <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
        <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
        <TextField source="landUse" label="Land Use (pt)" />
        <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
        <EllipsisTextField source="description" label="Description" />
        <EllipsisTextField source="primarySource" label="Primary source" />
        <EllipsisTextField source="dataGenerator" label="Data generator" />
        <EllipsisTextField source="impactGenerator" label="Impact generator" />
        <EllipsisTextField source="originalDataset" label="Original dataset" />
        <EllipsisTextField source="systemBoundaries" label="System boundaries" />
        <EllipsisTextField source="allocation" label="Allocation" />
        <EllipsisTextField source="mainAssumptions" label="Main assumptions" />
        <TextField source="dataValidity" label="Data validity" />
        <TextField source="dataQuality" label="Data quality" />
        <TextField source="referenceYear" label="Reference year" />
        <TextField source="teR" label="teR" />
        <TextField source="geR" label="geR" />
        <TextField source="tiR" label="tiR" />
        <TextField source="precision" label="Precision" />
        <TextField source="type" label="Type" />
        <ReferenceField label="Account" source="accountId" reference="accounts" emptyText="" link={false}>
          <FunctionField render={record => record ? `${record.name} (${record.id})` : ''} />
        </ReferenceField>
        <EditButton />
      </Datagrid>
    </div>
  </VersionedList>
);

const ShowFinishingOrigin = (props) => (
  <Show {...props} resource="processOrigin/finishing">
    <SimpleShowLayout>
      <TextField source="id" />
      <ReferenceField label="Name" source="finishingId" reference="process/finishing">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Origin" source="originId" reference="origins">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Database Context" source="databaseContextId" reference="database-context">
        <TextField source="name" />
      </ReferenceField>
      <TextField source="energyType" label="Energy Type" />
      <DateField source="createdAt" label="Creation date" />
      <DateField source="updatedAt" label="Update date" />
      <TextField source="water" label="Water scarcity (m3 water eq)" />
      <TextField source="waterConsumption" label="Water consumption (liters water)" />
      <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
      <TextField source="eutrophication" label="Eutrophication (g P eq)" />
      <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
      <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextField source="acidification" label="Acidification (mol H+ eq)" />
      <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextField source="landUse" label="Land Use (pt)" />
      <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
      <TextField source="description" label="Description" />
      <TextField source="primarySource" label="Primary source" />
      <TextField source="dataGenerator" label="Data generator" />
      <TextField source="impactGenerator" label="Impact generator" />
      <TextField source="originalDataset" label="Original dataset" />
      <TextField source="systemBoundaries" label="System boundaries" />
      <TextField source="allocation" label="Allocation" />
      <TextField source="mainAssumptions" label="Main assumptions" />
      <TextField source="dataValidity" label="Data validity" />
      <TextField source="dataQuality" label="Data quality" />
      <TextField source="referenceYear" label="Reference year" />
      <TextField source="teR" label="teR" />
      <TextField source="geR" label="geR" />
      <TextField source="tiR" label="tiR" />
      <TextField source="precision" label="Precision" />
      <TextField source="type" label="Type" />
      <ReferenceField label="Account" source="accountId" reference="accounts" emptyText="" link={false}>
        <FunctionField render={record => record ? `${record.name} (${record.id})` : ''} />
      </ReferenceField>
    </SimpleShowLayout>
  </Show>
);

const CreateFinishingOrigin = (props) => (
  <Create {...props} resource="processOrigin/finishing">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Name" source="finishingId" reference="process/finishing" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Database Context" source="databaseContextId" reference="database-context" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <SelectInput source="energyType" label="Energy Type" defaultValue="Conventional" choices={ENERGY_TYPE_OPTIONS} />
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={[
        { id: 'Generic', name: 'Generic' },
        { id: 'Custom', name: 'Custom' },
        { id: 'Specific', name: 'Specific' },
        { id: 'Multi-process', name: 'Multi-process' }
      ]} />
      <FormDataConsumer>
        {({ formData }) => formData.type === 'Custom' && (
          <ReferenceInput label="Account" source="accountId" reference="accounts" filterToQuery={searchText => ({ name: searchText })}>
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
        )}
      </FormDataConsumer>
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Create>
);

const EditFinishingOrigin = (props) => (
  <Edit {...props} resource="processOrigin/finishing">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Name" source="finishingId" reference="process/finishing" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Database Context" source="databaseContextId" reference="database-context" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <SelectInput source="energyType" label="Energy Type" defaultValue="Conventional" choices={ENERGY_TYPE_OPTIONS} />
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={[
        { id: 'Generic', name: 'Generic' },
        { id: 'Custom', name: 'Custom' },
        { id: 'Specific', name: 'Specific' },
        { id: 'Multi-process', name: 'Multi-process' }
      ]} />
      <FormDataConsumer>
        {({ formData }) => formData.type === 'Custom' && (
          <ReferenceInput label="Account" source="accountId" reference="accounts" filterToQuery={searchText => ({ name: searchText })}>
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
        )}
      </FormDataConsumer>
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Edit>
);

// Manufacturing Process Origin
const ManufacturingFilters = [
  <ReferenceInput
    key="manufacturingNameSearch"
    label="Search by name"
    source="manufacturingId"
    reference="process/manufacturing"
    alwaysOn
    filterToQuery={searchText => ({ name: searchText })}
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
  <ReferenceInput
    key="manufacturingAccountSearch"
    label="Search by account"
    source="accountId"
    reference="accounts"
    alwaysOn
    filterToQuery={searchText => ({ name: searchText })}
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
  <DbVersionFilterInput key="manufacturingOriginDbVersion" alwaysOn />
];

const ManufacturingOriginList = (props) => (
  <VersionedList {...props} resource="processOrigin/manufacturing" filters={ManufacturingFilters} exporter={makeProcessOriginExporter('manufacturing')}>
    <div style={{ marginTop: '15px' }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <ReferenceField label="Name" source="manufacturingId" reference="process/manufacturing">
          <TextField source="name" />
        </ReferenceField>
        <DbVersionField />
        <ReferenceField label="Origin" source="originId" reference="origins">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Database Context" source="databaseContextId" reference="database-context" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <TextField source="energyType" label="Energy Type" />
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="water" label="Water scarcity (m3 water eq)" />
        <TextField source="waterConsumption" label="Water consumption (liters water)" />
        <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
        <TextField source="eutrophication" label="Eutrophication (g P eq)" />
        <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
        <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
        <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
        <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
        <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
        <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
        <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
        <TextField source="acidification" label="Acidification (mol H+ eq)" />
        <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
        <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
        <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
        <TextField source="landUse" label="Land Use (pt)" />
        <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
        <EllipsisTextField source="description" label="Description" />
        <EllipsisTextField source="primarySource" label="Primary source" />
        <EllipsisTextField source="dataGenerator" label="Data generator" />
        <EllipsisTextField source="impactGenerator" label="Impact generator" />
        <EllipsisTextField source="originalDataset" label="Original dataset" />
        <EllipsisTextField source="systemBoundaries" label="System boundaries" />
        <EllipsisTextField source="allocation" label="Allocation" />
        <EllipsisTextField source="mainAssumptions" label="Main assumptions" />
        <TextField source="dataValidity" label="Data validity" />
        <TextField source="dataQuality" label="Data quality" />
        <TextField source="referenceYear" label="Reference year" />
        <TextField source="teR" label="teR" />
        <TextField source="geR" label="geR" />
        <TextField source="tiR" label="tiR" />
        <TextField source="precision" label="Precision" />
        <TextField source="type" label="Type" />
        <ReferenceField label="Account" source="accountId" reference="accounts" emptyText="" link={false}>
          <FunctionField render={record => record ? `${record.name} (${record.id})` : ''} />
        </ReferenceField>
        <EditButton />
      </Datagrid>
    </div>
  </VersionedList>
);

const ShowManufacturingOrigin = (props) => (
  <Show {...props} resource="processOrigin/manufacturing">
    <SimpleShowLayout>
      <TextField source="id" />
      <ReferenceField label="Name" source="manufacturingId" reference="process/manufacturing">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Origin" source="originId" reference="origins">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Database Context" source="databaseContextId" reference="database-context">
        <TextField source="name" />
      </ReferenceField>
      <TextField source="energyType" label="Energy Type" />
      <DateField source="createdAt" label="Creation date" />
      <DateField source="updatedAt" label="Update date" />
      <TextField source="water" label="Water scarcity (m3 water eq)" />
      <TextField source="waterConsumption" label="Water consumption (liters water)" />
      <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
      <TextField source="eutrophication" label="Eutrophication (g P eq)" />
      <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
      <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextField source="acidification" label="Acidification (mol H+ eq)" />
      <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextField source="landUse" label="Land Use (pt)" />
      <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
      <TextField source="description" label="Description" />
      <TextField source="primarySource" label="Primary source" />
      <TextField source="dataGenerator" label="Data generator" />
      <TextField source="impactGenerator" label="Impact generator" />
      <TextField source="originalDataset" label="Original dataset" />
      <TextField source="systemBoundaries" label="System boundaries" />
      <TextField source="allocation" label="Allocation" />
      <TextField source="mainAssumptions" label="Main assumptions" />
      <TextField source="dataValidity" label="Data validity" />
      <TextField source="dataQuality" label="Data quality" />
      <TextField source="referenceYear" label="Reference year" />
      <TextField source="teR" label="teR" />
      <TextField source="geR" label="geR" />
      <TextField source="tiR" label="tiR" />
      <TextField source="precision" label="Precision" />
      <TextField source="type" label="Type" />
      <ReferenceField label="Account" source="accountId" reference="accounts" emptyText="" link={false}>
        <FunctionField render={record => record ? `${record.name} (${record.id})` : ''} />
      </ReferenceField>
    </SimpleShowLayout>
  </Show>
);

const CreateManufacturingOrigin = (props) => (
  <Create {...props} resource="processOrigin/manufacturing">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Name" source="manufacturingId" reference="process/manufacturing" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Database Context" source="databaseContextId" reference="database-context" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <SelectInput source="energyType" label="Energy Type" defaultValue="Conventional" choices={ENERGY_TYPE_OPTIONS} />
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={[
        { id: 'Generic', name: 'Generic' },
        { id: 'Custom', name: 'Custom' },
        { id: 'Specific', name: 'Specific' },
        { id: 'Multi-process', name: 'Multi-process' }
      ]} />
      <FormDataConsumer>
        {({ formData }) => formData.type === 'Custom' && (
          <ReferenceInput label="Account" source="accountId" reference="accounts" filterToQuery={searchText => ({ name: searchText })}>
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
        )}
      </FormDataConsumer>
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Create>
);

const EditManufacturingOrigin = (props) => (
  <Edit {...props} resource="processOrigin/manufacturing">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Name" source="manufacturingId" reference="process/manufacturing" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Database Context" source="databaseContextId" reference="database-context" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <SelectInput source="energyType" label="Energy Type" defaultValue="Conventional" choices={ENERGY_TYPE_OPTIONS} />
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={[
        { id: 'Generic', name: 'Generic' },
        { id: 'Custom', name: 'Custom' },
        { id: 'Specific', name: 'Specific' },
        { id: 'Multi-process', name: 'Multi-process' }
      ]} />
      <FormDataConsumer>
        {({ formData }) => formData.type === 'Custom' && (
          <ReferenceInput label="Account" source="accountId" reference="accounts" filterToQuery={searchText => ({ name: searchText })}>
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
        )}
      </FormDataConsumer>
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Edit>
);

// Packaging Process Origin
const PackagingFilters = [
  <ReferenceInput
    key="packagingNameSearch"
    label="Search by name"
    source="packagingId"
    reference="process/packaging"
    alwaysOn
    filterToQuery={searchText => ({ name: searchText })}
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
  <DbVersionFilterInput key="packagingOriginDbVersion" alwaysOn />
];

const PackagingOriginList = (props) => (
  <VersionedList {...props} resource="processOrigin/packaging" filters={PackagingFilters} exporter={makeProcessOriginExporter('packaging')}>
    <div style={{ marginTop: '15px' }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <ReferenceField label="Name" source="packagingId" reference="process/packaging">
          <TextField source="name" />
        </ReferenceField>
        <DbVersionField />
        <ReferenceField label="Origin" source="originId" reference="origins">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Database Context" source="databaseContextId" reference="database-context" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="water" label="Water scarcity (m3 water eq)" />
        <TextField source="waterConsumption" label="Water consumption (liters water)" />
        <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
        <TextField source="eutrophication" label="Eutrophication (g P eq)" />
        <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
        <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
        <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
        <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
        <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
        <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
        <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
        <TextField source="acidification" label="Acidification (mol H+ eq)" />
        <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
        <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
        <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
        <TextField source="landUse" label="Land Use (pt)" />
        <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
        <EllipsisTextField source="description" label="Description" />
        <EllipsisTextField source="primarySource" label="Primary source" />
        <EllipsisTextField source="dataGenerator" label="Data generator" />
        <EllipsisTextField source="impactGenerator" label="Impact generator" />
        <EllipsisTextField source="originalDataset" label="Original dataset" />
        <EllipsisTextField source="systemBoundaries" label="System boundaries" />
        <EllipsisTextField source="allocation" label="Allocation" />
        <EllipsisTextField source="mainAssumptions" label="Main assumptions" />
        <TextField source="dataValidity" label="Data validity" />
        <TextField source="dataQuality" label="Data quality" />
        <TextField source="referenceYear" label="Reference year" />
        <TextField source="teR" label="teR" />
        <TextField source="geR" label="geR" />
        <TextField source="tiR" label="tiR" />
        <TextField source="precision" label="Precision" />
        <TextField source="type" label="Type" />
        <EditButton />
      </Datagrid>
    </div>
  </VersionedList>
);

const ShowPackagingOrigin = (props) => (
  <Show {...props} resource="processOrigin/packaging">
    <SimpleShowLayout>
      <TextField source="id" />
      <ReferenceField label="Name" source="packagingId" reference="process/packaging">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Origin" source="originId" reference="origins">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Database Context" source="databaseContextId" reference="database-context">
        <TextField source="name" />
      </ReferenceField>
      <DateField source="createdAt" label="Creation date" />
      <DateField source="updatedAt" label="Update date" />
      <TextField source="water" label="Water scarcity (m3 water eq)" />
      <TextField source="waterConsumption" label="Water consumption (liters water)" />
      <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
      <TextField source="eutrophication" label="Eutrophication (g P eq)" />
      <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
      <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextField source="acidification" label="Acidification (mol H+ eq)" />
      <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextField source="landUse" label="Land Use (pt)" />
      <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
      <TextField source="description" label="Description" />
      <TextField source="primarySource" label="Primary source" />
      <TextField source="dataGenerator" label="Data generator" />
      <TextField source="impactGenerator" label="Impact generator" />
      <TextField source="originalDataset" label="Original dataset" />
      <TextField source="systemBoundaries" label="System boundaries" />
      <TextField source="allocation" label="Allocation" />
      <TextField source="mainAssumptions" label="Main assumptions" />
      <TextField source="dataValidity" label="Data validity" />
      <TextField source="dataQuality" label="Data quality" />
      <TextField source="referenceYear" label="Reference year" />
      <TextField source="teR" label="teR" />
      <TextField source="geR" label="geR" />
      <TextField source="tiR" label="tiR" />
      <TextField source="precision" label="Precision" />
      <TextField source="type" label="Type" />
    </SimpleShowLayout>
  </Show>
);

const CreatePackagingOrigin = (props) => (
  <Create {...props} resource="processOrigin/packaging">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Name" source="packagingId" reference="process/packaging" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Database Context" source="databaseContextId" reference="database-context" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={[
        { id: 'Generic', name: 'Generic' },
        { id: 'Custom', name: 'Custom' },
        { id: 'Specific', name: 'Specific' },
        { id: 'Multi-process', name: 'Multi-process' }
      ]} />
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Create>
);

const EditPackagingOrigin = (props) => (
  <Edit {...props} resource="processOrigin/packaging">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Name" source="packagingId" reference="process/packaging" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Database Context" source="databaseContextId" reference="database-context" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={[
        { id: 'Generic', name: 'Generic' },
        { id: 'Custom', name: 'Custom' },
        { id: 'Specific', name: 'Specific' },
        { id: 'Multi-process', name: 'Multi-process' }
      ]} />
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Edit>
);

// Transport Process Origin
const TransportFilters = [
  <ReferenceInput
    key="transportNameSearch"
    label="Search by name"
    source="transportId"
    reference="process/transport"
    alwaysOn
    filterToQuery={searchText => ({ name: searchText })}
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
  <ReferenceInput
    key="transportAccountSearch"
    label="Search by account"
    source="accountId"
    reference="accounts"
    alwaysOn
    filterToQuery={searchText => ({ name: searchText })}
  >
    <AutocompleteInput optionText="name" />
  </ReferenceInput>,
  <DbVersionFilterInput key="transportOriginDbVersion" alwaysOn />
];

const TransportOriginList = (props) => (
  <VersionedList {...props} resource="processOrigin/transport" filters={TransportFilters} exporter={makeProcessOriginExporter('transport')}>
    <div style={{ marginTop: '15px' }}>
      <Datagrid rowClick="show">
        <TextField source="id" />
        <ReferenceField label="Name" source="transportId" reference="process/transport" cellClassName="name-column">
          <TextField source="name" />
        </ReferenceField>
        <DbVersionField />
        <ReferenceField label="Origin" source="originId" reference="origins">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="Database Context" source="databaseContextId" reference="database-context" emptyText="">
          <TextField source="name" />
        </ReferenceField>
        <DateField source="createdAt" label="Creation date" />
        <DateField source="updatedAt" label="Update date" />
        <TextField source="water" label="Water scarcity (m3 water eq)" />
        <TextField source="waterConsumption" label="Water consumption (liters water)" />
        <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
        <TextField source="eutrophication" label="Eutrophication (g P eq)" />
        <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
        <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
        <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
        <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
        <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
        <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
        <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
        <TextField source="acidification" label="Acidification (mol H+ eq)" />
        <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
        <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
        <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
        <TextField source="landUse" label="Land Use (pt)" />
        <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
        <EllipsisTextField source="description" label="Description" />
        <EllipsisTextField source="primarySource" label="Primary source" />
        <EllipsisTextField source="dataGenerator" label="Data generator" />
        <EllipsisTextField source="impactGenerator" label="Impact generator" />
        <EllipsisTextField source="originalDataset" label="Original dataset" />
        <EllipsisTextField source="systemBoundaries" label="System boundaries" />
        <EllipsisTextField source="allocation" label="Allocation" />
        <EllipsisTextField source="mainAssumptions" label="Main assumptions" />
        <TextField source="dataValidity" label="Data validity" />
        <TextField source="dataQuality" label="Data quality" />
        <TextField source="referenceYear" label="Reference year" />
        <TextField source="teR" label="teR" />
        <TextField source="geR" label="geR" />
        <TextField source="tiR" label="tiR" />
        <TextField source="precision" label="Precision" />
        <TextField source="type" label="Type" />
        <ReferenceField label="Account" source="accountId" reference="accounts" emptyText="" link={false}>
          <FunctionField render={record => record ? `${record.name} (${record.id})` : ''} />
        </ReferenceField>
        <EditButton />
      </Datagrid>
    </div>
  </VersionedList>
);

const ShowTransportOrigin = (props) => (
  <Show {...props} resource="processOrigin/transport">
    <SimpleShowLayout>
      <TextField source="id" />
      <ReferenceField label="Name" source="transportId" reference="process/transport">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Origin" source="originId" reference="origins">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField label="Database Context" source="databaseContextId" reference="database-context">
        <TextField source="name" />
      </ReferenceField>
      <DateField source="createdAt" label="Creation date" />
      <DateField source="updatedAt" label="Update date" />
      <TextField source="water" label="Water scarcity (m3 water eq)" />
      <TextField source="waterConsumption" label="Water consumption (liters water)" />
      <TextField source="climateChange" label="Global warming (kg CO2 eq)" />
      <TextField source="eutrophication" label="Eutrophication (g P eq)" />
      <TextField source="fossilFuels" label="Abiotic depletion (MJ)" />
      <TextField source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextField source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextField source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextField source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextField source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextField source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextField source="acidification" label="Acidification (mol H+ eq)" />
      <TextField source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextField source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextField source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextField source="landUse" label="Land Use (pt)" />
      <TextField source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
      <TextField source="description" label="Description" />
      <TextField source="primarySource" label="Primary source" />
      <TextField source="dataGenerator" label="Data generator" />
      <TextField source="impactGenerator" label="Impact generator" />
      <TextField source="originalDataset" label="Original dataset" />
      <TextField source="systemBoundaries" label="System boundaries" />
      <TextField source="allocation" label="Allocation" />
      <TextField source="mainAssumptions" label="Main assumptions" />
      <TextField source="dataValidity" label="Data validity" />
      <TextField source="dataQuality" label="Data quality" />
      <TextField source="referenceYear" label="Reference year" />
      <TextField source="teR" label="teR" />
      <TextField source="geR" label="geR" />
      <TextField source="tiR" label="tiR" />
      <TextField source="precision" label="Precision" />
      <TextField source="type" label="Type" />
      <ReferenceField label="Account" source="accountId" reference="accounts" emptyText="" link={false}>
        <FunctionField render={record => record ? `${record.name} (${record.id})` : ''} />
      </ReferenceField>
    </SimpleShowLayout>
  </Show>
);

const CreateTransportOrigin = (props) => (
  <Create {...props} resource="processOrigin/transport">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Name" source="transportId" reference="process/transport" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Database Context" source="databaseContextId" reference="database-context" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={[
        { id: 'Generic', name: 'Generic' },
        { id: 'Custom', name: 'Custom' },
        { id: 'Specific', name: 'Specific' },
        { id: 'Multi-process', name: 'Multi-process' }
      ]} />
      <FormDataConsumer>
        {({ formData }) => formData.type === 'Custom' && (
          <ReferenceInput label="Account" source="accountId" reference="accounts" filterToQuery={searchText => ({ name: searchText })}>
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
        )}
      </FormDataConsumer>
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Create>
);

const EditTransportOrigin = (props) => (
  <Edit {...props} resource="processOrigin/transport">
    <SimpleForm>
      <DbVersionInput />
      <ReferenceInput label="Name" source="transportId" reference="process/transport" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Origin" source="originId" reference="origins" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <ReferenceInput label="Database Context" source="databaseContextId" reference="database-context" filterToQuery={searchText => ({ name: searchText })}>
        <AutocompleteInput optionText="name" required={true} />
      </ReferenceInput>
      <TextInput source="description" label="Description" fullWidth multiline />
      <TextInput source="primarySource" label="Primary source" />
      <TextInput source="dataGenerator" label="Data generator" />
      <TextInput source="impactGenerator" label="Impact generator" />
      <TextInput source="originalDataset" label="Original dataset" fullWidth />
      <TextInput source="systemBoundaries" label="System boundaries" fullWidth multiline />
      <TextInput source="allocation" label="Allocation" fullWidth multiline />
      <TextInput source="mainAssumptions" label="Main assumptions" fullWidth multiline />
      <TextInput source="dataValidity" label="Data validity" />
      <TextInput source="dataQuality" label="Data quality" />
      <TextInput source="referenceYear" label="Reference year" />
      <TextInput source="teR" label="teR" />
      <TextInput source="geR" label="geR" />
      <TextInput source="tiR" label="tiR" />
      <TextInput source="precision" label="Precision" />
      <SelectInput source="type" label="Type" choices={[
        { id: 'Generic', name: 'Generic' },
        { id: 'Custom', name: 'Custom' },
        { id: 'Specific', name: 'Specific' },
        { id: 'Multi-process', name: 'Multi-process' }
      ]} />
      <FormDataConsumer>
        {({ formData }) => formData.type === 'Custom' && (
          <ReferenceInput label="Account" source="accountId" reference="accounts" filterToQuery={searchText => ({ name: searchText })}>
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
        )}
      </FormDataConsumer>
      <TextInput source="water" label="Water scarcity (m3 water eq)" required={true} />
      <TextInput source="waterConsumption" label="Water consumption (liters water)" />
      <TextInput source="climateChange" label="Global warming (kg CO2 eq)" required={true} />
      <TextInput source="eutrophication" label="Eutrophication (g P eq)" required={true} />
      <TextInput source="fossilFuels" label="Abiotic depletion (MJ)" required={true} />
      <TextInput source="ozoneDepletion" label="Ozone Depletion (kg CFC11 eq)" />
      <TextInput source="humanToxicityCancerEffects" label="Human Toxicity, Cancer Effects (CTUh)" />
      <TextInput source="humanToxicityNonCancerEffects" label="Human Toxicity, Non-Cancer Effects (CTUh)" />
      <TextInput source="particulateMatter" label="Particulate Matter (disease incidence)" />
      <TextInput source="ionizingRadiation" label="Ionizing Radiation (kBq U235 eq)" />
      <TextInput source="photochemicalOzoneFormation" label="Photochemical Ozone Formation (kg NMVOC eq)" />
      <TextInput source="acidification" label="Acidification (mol H+ eq)" />
      <TextInput source="terrestrialEutrophication" label="Terrestrial Eutrophication (mol N eq)" />
      <TextInput source="marineEutrophication" label="Marine Eutrophication (kg N eq)" />
      <TextInput source="freshwaterEcotoxicity" label="Freshwater Ecotoxicity (CTUe)" />
      <TextInput source="landUse" label="Land Use (pt)" />
      <TextInput source="resourceUseMineralAndMetals" label="Resource Use, Mineral and Metals (kg Sb eq)" />
    </SimpleForm>
  </Edit>
);

// Recursos individuales para cada tipo de proceso origen
// Extraction Process Origin
const extractionOriginResource = () => (
  <Resource
    name="processOrigin/extraction"
    options={{ label: 'Extraction', menuGroup: 'Process Origin' }}
    list={ExtractionOriginList}
    create={CreateExtractionOrigin}
    edit={EditExtractionOrigin}
    show={ShowExtractionOrigin}
    path="processOrigin/extraction"
  />
);

const processingOriginResource = () => (
  <Resource
    name="processOrigin/processing"
    options={{ label: 'Processing', menuGroup: 'Process Origin' }}
    list={ProcessingOriginList}
    create={CreateProcessingOrigin}
    edit={EditProcessingOrigin}
    show={ShowProcessingOrigin}
    path="processOrigin/processing"
  />
);

const finishingOriginResource = () => (
  <Resource
    name="processOrigin/finishing"
    options={{ label: 'Finishing', menuGroup: 'Process Origin' }}
    list={FinishingOriginList}
    create={CreateFinishingOrigin}
    edit={EditFinishingOrigin}
    show={ShowFinishingOrigin}
    path="processOrigin/finishing"
  />
);

const manufacturingOriginResource = () => (
  <Resource
    name="processOrigin/manufacturing"
    options={{ label: 'Manufacturing', menuGroup: 'Process Origin' }}
    list={ManufacturingOriginList}
    create={CreateManufacturingOrigin}
    edit={EditManufacturingOrigin}
    show={ShowManufacturingOrigin}
    path="processOrigin/manufacturing"
  />
);

const packagingOriginResource = () => (
  <Resource
    name="processOrigin/packaging"
    options={{ label: 'Packaging', menuGroup: 'Process Origin' }}
    list={PackagingOriginList}
    create={CreatePackagingOrigin}
    edit={EditPackagingOrigin}
    show={ShowPackagingOrigin}
    path="processOrigin/packaging"
  />
);

const transportOriginResource = () => (
  <Resource
    name="processOrigin/transport"
    options={{ label: 'Transport', menuGroup: 'Process Origin' }}
    list={TransportOriginList}
    create={CreateTransportOrigin}
    edit={EditTransportOrigin}
    show={ShowTransportOrigin}
    path="processOrigin/transport"
  />
);

// Exportar los recursos
export {
  extractionOriginResource,
  processingOriginResource,
  finishingOriginResource,
  manufacturingOriginResource,
  packagingOriginResource,
  transportOriginResource
};

const processOriginResource = () => (
  <Resource
    name="processOrigin"
    options={{ label: 'Process by origin', menuGroup: 'Bcome' }}
    list={ProcessOriginList}
    create={CreateProcessOrigin}
    edit={EditProcessOrigin}
    show={ShowProcessOrigin}
    path="processOrigin"
  />
);

export default processOriginResource;
